import React, { useEffect, useState } from "react";
import {
    Card,
    Row,
    Col,
    Button,
    Upload,
    Table,
    Typography,
    Space,
    Divider,
    message,
} from "antd";
import { AlertTriangle } from "lucide-react";
import {
    DownloadOutlined,
    InboxOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

import {
    getRevNoLogs,
    uploadRevNo,
    downloadRevNoTemplate,
} from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { getRevNoColumns } from "./RevNoColumn";
import CustomTable from "../../components/Common/CustomTable/CustomTable";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const UpdateRevNo = () => {
    const dispatch = useDispatch();

    const {
        revNoLogs,
        revNoLogsPagination,
        gettingRevNoLogs,
        uploadingRevNo,
        downloadingRevNoTemplate,
    } = useSelector((state) => state.unitReport);
    const [selectedFile, setSelectedFile] = useState(null);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
    });

    useEffect(() => {
        fetchLogs(1, 10);
    }, []);

    const fetchLogs = (page = 1, limit = 10) => {
        dispatch(
            getRevNoLogs({
                page,
                limit,
            })
        );
    };

    const handlePagination = (page, pageSize) => {
        setPagination({
            page,
            limit: pageSize,
        });

        fetchLogs(page, pageSize);
    };

    const beforeUpload = (file) => {
        const isExcel =
            file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel";

        if (!isExcel) {
            message.error("Please upload an Excel file.");
            return Upload.LIST_IGNORE;
        }

        setSelectedFile(file);

        return false;
    };

    const handleUpload = () => {
        if (!selectedFile) {
            message.warning("Please select an Excel file.");
            return;
        }

        const reader = new FileReader();

        reader.onload = async (e) => {
            const workbook = XLSX.read(e.target.result, {
                type: "binary",
            });

            const worksheet =
                workbook.Sheets[workbook.SheetNames[0]];

            // const excelData = XLSX.utils.sheet_to_json(worksheet);
            const excelData = XLSX.utils
                .sheet_to_json(worksheet)
                .filter(row =>
                    Object.values(row).some(
                        value => value !== undefined &&
                            value !== null &&
                            value.toString().trim() !== ""
                    )
                );

            const validationErrors = [];

            excelData.forEach((row, index) => {
                const rowNumber = index + 2; // Excel row number (header is row 1)

                const missingFields = [];

                if (!row.IO_NO?.toString().trim()) {
                    missingFields.push("IO No");
                }

                if (!row.REV_NO?.toString().trim()) {
                    missingFields.push("Old REV No");
                }

                if (!row.NEW_REV_NO?.toString().trim()) {
                    missingFields.push("New REV No");
                }

                if (missingFields.length) {
                    validationErrors.push(
                        `Row ${rowNumber}: ${missingFields.join(", ")} ${missingFields.length > 1 ? "are" : "is"} missing`
                    );
                }
            });

            if (validationErrors.length) {
                message.error({
                    content: (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                Upload aborted due to missing required fields:
                            </div>

                            {validationErrors.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    ),
                    duration: 8,
                });

                return;
            }

            dispatch(
                uploadRevNo({
                    data: excelData,
                })
            ).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setSelectedFile(null);

                    fetchLogs(
                        pagination.page,
                        pagination.limit
                    );
                }
            });
        };

        reader.readAsBinaryString(selectedFile);
    };

    const columns = [
        {
            title: "Sr. No.",
            width: 80,
            render: (_, __, index) =>
                (revNoLogsPagination.page - 1) *
                revNoLogsPagination.limit +
                index +
                1,
        },
        {
            title: "IO No",
            dataIndex: "IO_NO",
            key: "IO_NO",
        },
        {
            title: "Old Rev No",
            dataIndex: "OLD_REV_NO",
            key: "OLD_REV_NO",
        },
        {
            title: "New Rev No",
            dataIndex: "NEW_REV_NO",
            key: "NEW_REV_NO",
        },
        {
            title: "Rows Updated",
            dataIndex: "ROWS_AFFECTED",
            key: "ROWS_AFFECTED",
            align: "center",
        },
        {
            title: "Changed By",
            dataIndex: "CHANGED_BY",
            key: "CHANGED_BY",
        },
        {
            title: "Changed At",
            dataIndex: "CHANGED_AT",
            key: "CHANGED_AT",
            render: (value) =>
                dayjs(value).format("DD MMM YYYY hh:mm A"),
        },
    ];

    return (
        <div className="p-6">

            <div className="mb-6">
                <h1 className="text-[24px] font-semibold text-[#344054]">
                    Update REV No.
                </h1>
                <p className="text-[14px] text-[#667085] mt-1">
                    Download the template, update revision numbers and upload the file.
                </p>
            </div>

            <div className="mb-6 rounded-lg border border-[#FDA29B] bg-[#FEF3F2] px-4 py-3">
                <div className="flex items-start gap-3">
                    <AlertTriangle
                        size={24}
                        className="text-[#D92D20] mt-0.5 flex-shrink-0"
                    />

                    <div className="text-sm leading-6 text-[#B42318]">
                        <span className="font-bold">Important:</span>{" "}
                        The <span className="font-bold">REV No.</span> changes
                        <span className="font-bold"> cannot be reverted. </span>
                        {/* <br /> */}
                        Upload an Excel file containing{" "}
                        <span className="font-bold">
                            only the REV No. entries that need to be updated.
                        </span>
                    </div>
                </div>
            </div>
            <Row gutter={16}>

                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span className="text-[#1570EF] font-semibold text-base">
                                Download Template
                            </span>
                        }
                        className="rounded-xl shadow-sm border border-[#EAECF0] h-full"
                    >
                        <Space
                            onClick={() => dispatch(downloadRevNoTemplate())}
                            direction="vertical" style={{ width: "100%" }}>
                            <Dragger
                                showUploadList={false}
                                onClick={() => dispatch(downloadRevNoTemplate())}
                                openFileDialogOnClick={false}
                                className="cursor-default"
                            >
                                <p className="ant-upload-drag-icon ">
                                    <DownloadOutlined />
                                </p>

                                <p className="ant-upload-text">
                                    Download the REV No. template
                                </p>

                                <p className="ant-upload-hint">
                                    Update only the required REV No. entries.
                                </p>
                            </Dragger>

                            <Button
                                block
                                type="primary"
                                icon={<DownloadOutlined />}
                                loading={downloadingRevNoTemplate}
                                onClick={() => dispatch(downloadRevNoTemplate())}
                            >
                                Download Template
                            </Button>
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span className="text-[#1570EF] font-semibold text-base">
                                Upload REV No
                            </span>
                        }
                        className="rounded-xl shadow-sm border border-[#EAECF0] h-full"
                    >

                        <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                        >

                            <Dragger
                                beforeUpload={beforeUpload}
                                maxCount={1}
                                accept=".xlsx,.xls"
                                fileList={
                                    selectedFile ? [selectedFile] : []
                                }
                                onRemove={() => {
                                    setSelectedFile(null);
                                }}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>

                                <p className="ant-upload-text">
                                    Click or drag Excel file here
                                </p>

                                <p className="ant-upload-hint">
                                    Only .xlsx and .xls files are supported.
                                </p>

                            </Dragger>

                            <Button
                                block
                                type="primary"
                                icon={<UploadOutlined />}
                                loading={uploadingRevNo}
                                disabled={!selectedFile}
                                onClick={handleUpload}
                            >
                                Upload REV No
                            </Button>

                        </Space>

                    </Card>
                </Col>

            </Row>

            <Divider />
            <Card title="REV No Update Logs">
                <CustomTable
                    rowKey="ID"
                    loading={gettingRevNoLogs}
                    columns={getRevNoColumns(
                        revNoLogsPagination?.page || pagination.page,
                        revNoLogsPagination?.limit || pagination.limit
                    )}
                    dataSource={revNoLogs}
                    pagination={{
                        current: revNoLogsPagination?.page || pagination.page,
                        pageSize: revNoLogsPagination?.limit || pagination.limit,
                        total: revNoLogsPagination?.total || 0,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} records`,
                        onChange: handlePagination,
                        onShowSizeChange: handlePagination,
                    }}
                />
            </Card>
        </div>
    );
};

export default UpdateRevNo;